import { supabaseClient } from 'supabase'
import { DeskQueueRow, RawItemRow } from './deskTypes'

export const PASTE_RESULT_RAW_COLUMNS = 'id, status, status_reason, source, image_path'

export const PASTE_RESULT_CANDIDATE_COLUMNS =
  'application_id, event_index, event_title, starting_date, starting_time, route, match_label, desk_tab, source'

/** One candidate the editor made of a paste, with the tab it sits in now. */
export type PasteCandidate = Pick<
  DeskQueueRow,
  'event_index' | 'event_title' | 'starting_date' | 'starting_time' | 'route' | 'match_label' | 'desk_tab' | 'source'
> & { application_id: number }

/** A pasted raw item and its candidates: what the paste panel polls. */
export type PasteResult = Pick<RawItemRow, 'id' | 'status' | 'status_reason' | 'source' | 'image_path'> & {
  candidates: PasteCandidate[]
}

/**
 * The raw item a paste made, and every editor candidate of it in the
 * editor's order.
 *
 * The candidates come from `desk_queue`: its candidate branch is exactly the
 * applications with `editor_version` not null, joined to the tab each one
 * sits in now (NULL once decided). A raw item the editor discarded whole has
 * its own row there with no application_id, filtered out with `.not(...)`
 * (never `.eq(col, null)`, which PostgREST never matches).
 *
 * Admin-only through RLS on both reads.
 */
export const queryPasteResult = async (rawItemId: number): Promise<PasteResult> => {
  const { data: raw, error: rawError } = await supabaseClient
    .from('raw_items')
    .select(PASTE_RESULT_RAW_COLUMNS)
    .eq('id', rawItemId)
    .single()

  if (rawError) {
    throw rawError
  }

  const { data: candidates, error: candidatesError } = await supabaseClient
    .from('desk_queue')
    .select(PASTE_RESULT_CANDIDATE_COLUMNS)
    .eq('raw_item_id', rawItemId)
    .not('application_id', 'is', null)
    .order('event_index')

  if (candidatesError) {
    throw candidatesError
  }

  return {
    ...raw,
    candidates: (candidates ?? []).filter(
      (candidate): candidate is PasteCandidate => candidate.application_id !== null
    )
  }
}
